import { useState, useMemo, useEffect } from 'react';
import { useEmailThread } from 'src/api/communications/email-threads/useEmailThread';
import type { EmailThreadEmail } from '@erp/rpc';

export interface UseEmailThreadViewerOptions {
  conversationId: string | null;
  onEmailSelect?: (email: EmailThreadEmail) => void;
}

export function useEmailThreadViewer({
  conversationId,
  onEmailSelect,
}: UseEmailThreadViewerOptions) {
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());

  // Fetch thread data
  const { data: threadData, isLoading, error } = useEmailThread(conversationId);

  // Get selected email
  const selectedEmail = useMemo(() => {
    if (!threadData?.emails || !selectedEmailId) return null;
    return (
      threadData.emails.find((email) => email.id === selectedEmailId) || null
    );
  }, [threadData?.emails, selectedEmailId]);

  // Get latest email (for initial display)
  const latestEmail = useMemo(() => {
    if (!threadData?.emails?.length) return null;
    return threadData.emails[threadData.emails.length - 1];
  }, [threadData?.emails]);

  // Automatically expand the latest email when thread data loads
  useEffect(() => {
    if (latestEmail) {
      setExpandedEmails(new Set([latestEmail.id]));
    }
  }, [latestEmail]);

  // Email selection handler
  const handleEmailSelect = (email: EmailThreadEmail) => {
    setSelectedEmailId(email.id);
    onEmailSelect?.(email);
  };

  // Email expansion handlers
  const toggleEmailExpanded = (emailId: string) => {
    setExpandedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const expandAllEmails = () => {
    if (!threadData?.emails) return;
    setExpandedEmails(new Set(threadData.emails.map((email) => email.id)));
  };

  const collapseAllEmails = () => {
    setExpandedEmails(new Set());
  };

  // Thread metadata
  const threadMetadata = useMemo(() => {
    if (!threadData) return null;

    return {
      totalCount: threadData.totalCount,
      participantEmails: threadData.participantEmails,
      earliestDate: new Date(threadData.earliestDate),
      latestDate: new Date(threadData.latestDate),
      conversationId: threadData.conversationId,
    };
  }, [threadData]);

  // All thread attachments count
  const totalAttachments = useMemo(() => {
    if (!threadData?.emails) return 0;
    return threadData.emails.reduce((total, email) => {
      return total + (email.attachmentCount || 0);
    }, 0);
  }, [threadData?.emails]);

  return {
    // Data
    threadData,
    selectedEmail,
    latestEmail,
    threadMetadata,
    totalAttachments,

    // State
    isLoading,
    error,
    selectedEmailId,
    expandedEmails,

    // Actions
    handleEmailSelect,
    toggleEmailExpanded,
    expandAllEmails,
    collapseAllEmails,
  };
}
